/* =========================================================
   ALPENGRAVUR — GLOBAL THEME SCRIPT
   Shared engines used across sections: engraving canvas render,
   parallax, 3D tilt, magnetic buttons, reveal-on-scroll, nav,
   mobile menu, cart drawer / AJAX cart.
========================================================== */
window.AG = (function(){
  "use strict";

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = window.matchMedia('(pointer: fine)').matches && window.matchMedia('(min-width: 820px)').matches;
  const parallaxEnabled = !document.body.classList.contains('parallax-off');

  /* ---------------- grain overlay (SVG turbulence, generated once) ---------------- */
  const grainEl = document.getElementById('grainOverlay');
  /* the full-screen blended noise is expensive; skip it on phones/tablets */
  if(grainEl && window.matchMedia('(min-width: 900px) and (pointer: fine)').matches){
    grainEl.innerHTML =
      '<svg width="100%" height="100%"><filter id="noiseF"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter><rect width="100%" height="100%" filter="url(#noiseF)"/></svg>';
  }

  /* ============================================================
     ENGRAVING RENDER ENGINE (canvas) — used on homepage teaser + PDP
  ============================================================= */
  const WOODS = {
    beech:  {a:'#C9A06B', b:'#8C6337', grain:'#7A5228'},
    oak:    {a:'#B98A54', b:'#7A5326', grain:'#5E3E1C'},
    cherry: {a:'#B9694A', b:'#7A3E29', grain:'#5E2E1C'},
    alder:  {a:'#D2A574', b:'#96703F', grain:'#7A5228'},
    pine:   {a:'#E3CC9C', b:'#B0925F', grain:'#8C7040'},
    steel:  {a:'#C7CCCE', b:'#8A9194', grain:'#6E7477'}
  };

  function seededGrain(seed, w, h, count){
    let s = seed;
    const rand = () => { s = (s*9301+49297)%233280; return s/233280; };
    const lines = [];
    for(let i=0;i<count;i++){
      const y = rand()*h;
      const amp = 6+rand()*14;
      lines.push({y, amp, phase:rand()*Math.PI*2, op:0.08+rand()*0.14});
    }
    return lines;
  }

  function roundRect(ctx,x,y,w,h,r){
    ctx.beginPath();
    ctx.moveTo(x+r,y);
    ctx.arcTo(x+w,y,x+w,y+h,r);
    ctx.arcTo(x+w,y+h,x,y+h,r);
    ctx.arcTo(x,y+h,x,y,r);
    ctx.arcTo(x,y,x+w,y,r);
    ctx.closePath();
  }

  function drawBoard(ctx, w, h, wood, grainLines){
    const g = ctx.createLinearGradient(0,0,w,h);
    g.addColorStop(0, wood.a); g.addColorStop(1, wood.b);
    ctx.fillStyle = g;
    roundRect(ctx, 0,0,w,h, Math.min(w,h)*0.06);
    ctx.fill();
    ctx.save();
    roundRect(ctx,0,0,w,h,Math.min(w,h)*0.06); ctx.clip();
    ctx.strokeStyle = wood.grain;
    grainLines.forEach(l=>{
      ctx.globalAlpha = l.op;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      for(let x=0;x<=w;x+=14){
        const yy = l.y + Math.sin(x*0.02 + l.phase)*l.amp*0.3;
        x===0 ? ctx.moveTo(x,yy) : ctx.lineTo(x,yy);
      }
      ctx.stroke();
    });
    ctx.globalAlpha=1;
    const vg = ctx.createRadialGradient(w*0.5,h*0.4,h*0.2,w*0.5,h*0.5,h*0.9);
    vg.addColorStop(0,'rgba(0,0,0,0)'); vg.addColorStop(1,'rgba(0,0,0,0.22)');
    ctx.fillStyle = vg; ctx.fillRect(0,0,w,h);
    ctx.restore();
  }

  function drawEngravedText(ctx, w, h, text, reveal, fontSize){
    const cx = w/2, cy = h*0.54;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0,0, w*reveal, h);
    ctx.clip();
    ctx.textAlign='center'; ctx.textBaseline='middle';
    ctx.font = `italic 500 ${fontSize}px Fraunces, Georgia, serif`;
    ctx.shadowColor='rgba(0,0,0,.55)'; ctx.shadowBlur=3; ctx.shadowOffsetX=1.5; ctx.shadowOffsetY=2;
    ctx.fillStyle='rgba(15,12,9,.72)';
    ctx.fillText(text, cx, cy);
    ctx.shadowColor='transparent';
    ctx.fillStyle='rgba(255,244,222,.5)';
    ctx.fillText(text, cx-1, cy-1.4);
    ctx.restore();

    if(reveal>0.02 && reveal<0.995){
      const lx = w*reveal;
      const lg = ctx.createLinearGradient(lx-16,0,lx+16,0);
      lg.addColorStop(0,'rgba(217,168,94,0)'); lg.addColorStop(0.5,'rgba(255,214,150,.95)'); lg.addColorStop(1,'rgba(217,168,94,0)');
      ctx.fillStyle=lg;
      ctx.fillRect(lx-16,0,32,h);
    }
  }

  function makeEngraver(canvas, opts){
    if(!canvas) return { setText(){}, setWood(){} };
    const dpr = Math.min(window.devicePixelRatio||1, 2);
    const cssW = opts.cssW, cssH = opts.cssH;
    canvas.width = cssW*dpr; canvas.height = cssH*dpr;
    canvas.style.width='100%'; canvas.style.height='auto';
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr,0,0,dpr,0,0);
    const grain = seededGrain(opts.seed||7, cssW, cssH, 16);
    let wood = WOODS[opts.wood] || WOODS.beech;
    let particles = [];
    let revealTarget = 1, reveal = 0, raf = null, lastText='';

    function spawnParticles(x, h){
      for(let i=0;i<3;i++){
        particles.push({x:x+(Math.random()-0.5)*6, y:h*0.5+(Math.random()-0.5)*40, vx:(Math.random()-0.3)*0.6, vy:-0.3-Math.random()*0.6, life:1, r:1+Math.random()*1.8});
      }
    }

    function frame(){
      if(reduceMotion){ reveal = revealTarget; } else { reveal += (revealTarget-reveal)*0.14; }
      if(Math.abs(revealTarget-reveal)<0.003) reveal = revealTarget;
      drawBoard(ctx, cssW, cssH, wood, grain);
      drawEngravedText(ctx, cssW, cssH, lastText, reveal, opts.fontSize);
      if(!reduceMotion && (reveal < revealTarget - 0.003 || reveal < 0.995)){
        spawnParticles(cssW*reveal, cssH);
      }
      particles.forEach(p=>{ p.x+=p.vx; p.y+=p.vy; p.life-=0.02; });
      particles = particles.filter(p=>p.life>0);
      ctx.save();
      particles.forEach(p=>{
        ctx.globalAlpha = Math.max(p.life,0)*0.8;
        ctx.fillStyle = '#F4D9A6';
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2); ctx.fill();
      });
      ctx.restore();

      if(reveal < revealTarget-0.003 || particles.length){
        raf = requestAnimationFrame(frame);
      } else { raf = null; }
    }

    function setText(text){
      lastText = (text || '').slice(0, opts.maxLength || 25);
      reveal = 0; revealTarget = 1;
      if(!raf) raf = requestAnimationFrame(frame);
    }
    function setWood(key){
      wood = WOODS[key] || WOODS.beech;
      reveal = 0; revealTarget = 1;
      if(!raf) raf = requestAnimationFrame(frame);
    }
    setText(opts.initial || '');
    return { setText, setWood };
  }

  /* ============================================================
     PARALLAX ENGINE
  ============================================================= */
  function initParallax(){
    if(reduceMotion || !parallaxEnabled || !isDesktop) return;
    const parallaxEls = [...document.querySelectorAll('[data-parallax]')].map(el=>({el, speed:parseFloat(el.dataset.parallax)}));
    if(!parallaxEls.length) return;
    let ticking = false;
    function update(){
      const vh = window.innerHeight;
      parallaxEls.forEach(({el,speed})=>{
        const r = el.getBoundingClientRect();
        const center = r.top + r.height/2;
        const delta = Math.max(-140, Math.min(140, (vh/2 - center) * speed));
        el.style.setProperty('--parallax-y', delta.toFixed(1)+'px');
      });
      ticking = false;
    }
    window.addEventListener('scroll', ()=>{
      if(!ticking){ ticking = true; requestAnimationFrame(update); }
    }, {passive:true});
    update();
  }

  /* ============================================================
     3D TILT (mouse-reactive cards)
  ============================================================= */
  function initTilt(root){
    if(!isDesktop || reduceMotion) return;
    (root||document).querySelectorAll('[data-tilt]').forEach(card=>{
      if(card.dataset.tiltBound) return;
      card.dataset.tiltBound = '1';
      card.addEventListener('mousemove', e=>{
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left)/r.width - 0.5;
        const py = (e.clientY - r.top)/r.height - 0.5;
        card.style.transform = `rotateY(${px*10}deg) rotateX(${-py*10}deg) translateY(-4px)`;
      });
      card.addEventListener('mouseleave', ()=>{ card.style.transform = ''; });
    });
  }

  /* ============================================================
     MAGNETIC BUTTONS
  ============================================================= */
  function initMagnetic(root){
    if(!isDesktop || reduceMotion) return;
    (root||document).querySelectorAll('.magnetic').forEach(wrap=>{
      if(wrap.dataset.magBound) return;
      wrap.dataset.magBound = '1';
      const target = wrap.querySelector('a,button');
      if(!target) return;
      wrap.addEventListener('mousemove', e=>{
        const r = wrap.getBoundingClientRect();
        const x = e.clientX - r.left - r.width/2;
        const y = e.clientY - r.top - r.height/2;
        target.style.transform = `translate(${x*0.22}px, ${y*0.35}px)`;
      });
      wrap.addEventListener('mouseleave', ()=>{ target.style.transform=''; });
    });
  }

  /* ============================================================
     REVEAL ON SCROLL
  ============================================================= */
  function initReveal(root){
    const els = (root||document).querySelectorAll('.reveal:not([data-reveal-bound])');
    if(!els.length) return;
    if(!reduceMotion && 'IntersectionObserver' in window){
      const io = new IntersectionObserver((entries)=>{
        entries.forEach(e=>{ if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); } });
      }, {threshold:0.14});
      els.forEach(el=>{ el.dataset.revealBound='1'; io.observe(el); });
    } else {
      els.forEach(el=>{ el.dataset.revealBound='1'; el.classList.add('in'); });
    }
  }

  /* ============================================================
     NAV + MOBILE MENU
  ============================================================= */
  function initNav(){
    const navEl = document.getElementById('AGNav');
    if(navEl){
      const onScroll = () => navEl.classList.toggle('scrolled', window.scrollY > 80);
      window.addEventListener('scroll', onScroll, {passive:true});
      onScroll();
    }
    const mobileMenu = document.getElementById('mobileMenu');
    const burgerOpen = document.getElementById('burgerOpen');
    const burgerClose = document.getElementById('burgerClose');
    if(mobileMenu && burgerOpen && burgerClose){
      burgerOpen.addEventListener('click', ()=>{ mobileMenu.classList.add('open'); burgerOpen.setAttribute('aria-expanded','true'); });
      burgerClose.addEventListener('click', ()=>{ mobileMenu.classList.remove('open'); burgerOpen.setAttribute('aria-expanded','false'); });
      mobileMenu.querySelectorAll('a').forEach(a=>a.addEventListener('click', ()=>mobileMenu.classList.remove('open')));
    }
  }

  /* ============================================================
     MONEY FORMATTING (mirrors Shopify's classic formatMoney)
  ============================================================= */
  function formatMoney(cents, format){
    format = format || (window.Shopify && window.Shopify.money_format) || '€{{amount}}';
    if(typeof cents === 'string') cents = cents.replace('.','');
    function defaultTo(value, def){ return (value == null || value !== value) ? def : value; }
    function formatWithDelimiters(number, precision, thousands, decimal){
      precision = defaultTo(precision, 2); thousands = defaultTo(thousands, ','); decimal = defaultTo(decimal, '.');
      if(isNaN(number) || number == null) return 0;
      number = (number/100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1'+thousands);
      const cents2 = parts[1] ? decimal+parts[1] : '';
      return dollars+cents2;
    }
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    let value = '';
    switch(match && match[1]){
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /* ============================================================
     CART (AJAX) — shared by product forms, cart drawer, cart page
  ============================================================= */
  const cart = {
    async get(){
      const res = await fetch('/cart.js');
      return res.json();
    },
    async add(items){
      const res = await fetch((window.AGRoutes && window.AGRoutes.cartAdd) || '/cart/add.js', {
        method:'POST',
        headers:{'Content-Type':'application/json', 'Accept':'application/json'},
        body: JSON.stringify({items})
      });
      if(!res.ok){
        const err = await res.json().catch(()=>({}));
        throw new Error(err.description || (window.AGStrings && window.AGStrings.cartError) || 'cart_add_failed');
      }
      const data = await res.json();
      await this.refreshUI();
      return data;
    },
    async change(line, quantity){
      const res = await fetch((window.AGRoutes && window.AGRoutes.cartChange) || '/cart/change.js', {
        method:'POST',
        headers:{'Content-Type':'application/json', 'Accept':'application/json'},
        body: JSON.stringify({line, quantity})
      });
      const data = await res.json();
      await this.refreshUI();
      return data;
    },
    async refreshUI(){
      const state = await this.get();
      document.querySelectorAll('[data-cart-count]').forEach(el=>{
        el.textContent = state.item_count;
        el.hidden = state.item_count === 0;
      });
      renderCartDrawer(state);
      document.dispatchEvent(new CustomEvent('cart:updated', {detail: state}));
      return state;
    },
    open(){
      const drawer = document.getElementById('CartDrawer');
      const overlay = document.getElementById('CartDrawerOverlay');
      if(!drawer) return;
      drawer.classList.add('open'); drawer.setAttribute('aria-hidden','false');
      if(overlay){ overlay.hidden = false; requestAnimationFrame(()=>overlay.classList.add('open')); }
    },
    close(){
      const drawer = document.getElementById('CartDrawer');
      const overlay = document.getElementById('CartDrawerOverlay');
      if(!drawer) return;
      drawer.classList.remove('open'); drawer.setAttribute('aria-hidden','true');
      if(overlay){ overlay.classList.remove('open'); setTimeout(()=>{ overlay.hidden = true; }, 350); }
    }
  };

  function renderCartDrawer(state){
    const linesEl = document.getElementById('CartDrawerLines');
    const emptyEl = document.getElementById('CartDrawerEmpty');
    const footEl = document.getElementById('CartDrawerFoot');
    const subtotalEl = document.getElementById('CartDrawerSubtotal');
    const tpl = document.getElementById('CartLineTemplate');
    if(!linesEl || !tpl) return;

    if(state.item_count === 0){
      linesEl.hidden = true; if(emptyEl) emptyEl.hidden = false; if(footEl) footEl.hidden = true;
      linesEl.innerHTML = '';
      return;
    }
    if(emptyEl) emptyEl.hidden = true;
    linesEl.hidden = false;
    if(footEl) footEl.hidden = false;
    if(subtotalEl) subtotalEl.textContent = formatMoney(state.total_price);

    linesEl.innerHTML = '';
    state.items.forEach((line, index)=>{
      const node = tpl.content.cloneNode(true);
      const img = node.querySelector('.cart-line-thumb img');
      const thumbLink = node.querySelector('.cart-line-thumb');
      const titleLink = node.querySelector('.cart-line-title');
      const variantEl = node.querySelector('.cart-line-variant');
      const propsEl = node.querySelector('.cart-line-props');
      const stepper = node.querySelector('.qty-stepper');
      const qtyVal = node.querySelector('.qty-value');
      const priceEl = node.querySelector('.cart-line-price');
      const removeBtn = node.querySelector('[data-cart-remove]');

      if(img && line.image){ img.src = line.image; img.alt = line.title; }
      if(thumbLink) thumbLink.href = line.url;
      if(titleLink){ titleLink.href = line.url; titleLink.textContent = line.product_title || line.title; }
      if(variantEl) variantEl.textContent = (line.variant_title && line.variant_title !== 'Default Title') ? line.variant_title : '';
      if(propsEl && line.properties){
        const entries = Object.entries(line.properties).filter(([k,v]) => v && !k.startsWith('_'));
        propsEl.textContent = entries.map(([k,v]) => `${k}: ${v}`).join(' · ');
      }
      if(stepper) stepper.dataset.line = index+1;
      if(qtyVal) qtyVal.textContent = line.quantity;
      if(priceEl) priceEl.textContent = formatMoney(line.final_line_price);
      if(removeBtn) removeBtn.dataset.line = index+1;

      linesEl.appendChild(node);
    });
  }

  function initDrawers(){
    const cartOpenBtn = document.getElementById('CartOpen');
    const cartCloseBtn = document.getElementById('CartDrawerClose');
    const cartOverlay = document.getElementById('CartDrawerOverlay');
    if(cartOpenBtn && document.getElementById('CartDrawer')){
      cartOpenBtn.addEventListener('click', e=>{ e.preventDefault(); cart.open(); });
    }
    if(cartCloseBtn) cartCloseBtn.addEventListener('click', ()=>cart.close());
    if(cartOverlay) cartOverlay.addEventListener('click', ()=>cart.close());

    const linesEl = document.getElementById('CartDrawerLines');
    if(linesEl){
      linesEl.addEventListener('click', async (e)=>{
        const stepBtn = e.target.closest('.qty-btn');
        const removeBtn = e.target.closest('[data-cart-remove]');
        if(stepBtn){
          const stepper = stepBtn.closest('.qty-stepper');
          const line = parseInt(stepper.dataset.line, 10);
          const valEl = stepper.querySelector('.qty-value');
          let qty = parseInt(valEl.textContent, 10);
          qty += stepBtn.hasAttribute('data-qty-increase') ? 1 : -1;
          if(qty < 0) return;
          await cart.change(line, qty);
        } else if(removeBtn){
          const line = parseInt(removeBtn.dataset.line, 10);
          await cart.change(line, 0);
        }
      });
    }

    const searchOpenBtn = document.getElementById('SearchOpen');
    const searchCloseBtn = document.getElementById('SearchDrawerClose');
    const searchOverlay = document.getElementById('SearchDrawerOverlay');
    const searchDrawer = document.getElementById('SearchDrawer');
    if(searchOpenBtn) searchOpenBtn.setAttribute('aria-expanded', 'false');
    function openSearch(){
      if(!searchDrawer) return;
      searchDrawer.classList.add('open');
      searchDrawer.setAttribute('aria-hidden', 'false');
      if(searchOpenBtn) searchOpenBtn.setAttribute('aria-expanded', 'true');
      const input = document.getElementById('SearchDrawerInput');
      if(input) setTimeout(()=>input.focus(), 100);
    }
    if(searchOpenBtn && searchDrawer){
      searchOpenBtn.addEventListener('click', openSearch);
    }
    function closeSearch(){
      if(!searchDrawer) return;
      const wasOpen = searchDrawer.classList.contains('open');
      searchDrawer.classList.remove('open');
      searchDrawer.setAttribute('aria-hidden', 'true');
      if(searchOpenBtn){
        searchOpenBtn.setAttribute('aria-expanded', 'false');
        if(wasOpen) searchOpenBtn.focus();
      }
    }
    if(searchCloseBtn) searchCloseBtn.addEventListener('click', closeSearch);
    if(searchOverlay) searchOverlay.addEventListener('click', closeSearch);
    document.addEventListener('keydown', e=>{
      if(e.key === 'Escape'){ cart.close(); closeSearch(); }
    });
  }

  /* ============================================================
     BOOT
  ============================================================= */
  function boot(root){
    initReveal(root);
    initTilt(root);
    initMagnetic(root);
  }

  document.addEventListener('DOMContentLoaded', ()=>{
    initNav();
    initDrawers();
    initParallax();
    boot(document);
    cart.refreshUI().catch(()=>{});
  });

  return {
    reduceMotion, isDesktop, WOODS, seededGrain, drawBoard, drawEngravedText, roundRect,
    makeEngraver, initTilt, initMagnetic, initReveal, boot, cart, formatMoney
  };
})();
